import { useState, useMemo } from "react";
import {
  Modal, Button, TextField, Notice, Badge, Card,
  Tabs, TabBar, Tab, TabPanel, Icon, type BadgeTone,
} from "@conuti-das/prince-ui";
import { Flex, Stack, princeIcon } from "@/ui-prince";
import { marked } from "marked";
import type { Ticket, ReviewDecision } from "@/types/tickets";

marked.setOptions({ gfm: true, breaks: false });

// Liefert den Markdown ab "## User Story" (Anfangs-Boilerplate wird abgeschnitten).
function trimToUserStory(body: string): string {
  const idx = body.search(/^##\s+User Story\b/m);
  return idx >= 0 ? body.slice(idx) : body;
}

// Pfad relativ zur Vite-Middleware /source-docs/<doctype>/<formatversion>/<file>
function sourceDocHref(ticket: Ticket): string | null {
  if (!ticket.source_file) return null;
  const fv = ticket.formatversion || "";
  const dt = (ticket.doctype || "").toLowerCase();
  if (!fv || !dt) return null;
  return `/source-docs/${encodeURIComponent(dt)}/${encodeURIComponent(fv)}/${encodeURIComponent(ticket.source_file)}`;
}

type Props = {
  ticket: Ticket;
  decision?: ReviewDecision;
  onClose: () => void;
  /** "Approve & sofort in JIRA anlegen" -- ruft den Runner-Sync. */
  onApproveAndSync: (ticket: Ticket, edits: { title?: string; description?: string }) => void | Promise<void>;
  /** Nur lokal approveen (alter Workflow: Export + CLI). */
  onApprove: (edits?: { title?: string; description?: string }) => void;
  onReject: (reason: string) => void;
  onReset: () => void;
};

const RELEVANCE_TO_TONE: Record<string, BadgeTone> = {
  kritisch: "red",
  hoch: "orange",
  normal: "blue",
  gering: "gray",
  unbewertet: "gray",
};

export function TicketReviewDialog({ ticket, decision, onClose, onApprove, onApproveAndSync, onReject, onReset }: Props) {
  const originalTitle = ticket.body.split("\n").find((l) => l.startsWith("# "))?.slice(2).trim() ?? "";
  const [title, setTitle] = useState(decision?.edits?.title ?? originalTitle);
  const [description, setDescription] = useState(decision?.edits?.description ?? ticket.body);
  const [rejectMode, setRejectMode] = useState(false);
  const [rejectReason, setRejectReason] = useState(decision?.reject_reason ?? "");
  const [tab, setTab] = useState<"edit" | "meta" | "diff">("diff");
  const [docPreview, setDocPreview] = useState<string | null>(null);

  const previewHtml = useMemo(
    () => marked.parse(trimToUserStory(description)) as string,
    [description],
  );
  const docHref = sourceDocHref(ticket);

  const titleChanged = title !== originalTitle;
  const descChanged = description !== ticket.body;

  return (
    <Modal
      isOpen
      onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}
      title={`Review · ${ticket.aend_id} · ${ticket.doctype.toUpperCase()}`}
      className="prn-dialog-wide"
    >
      {/* Header-Info */}
      <Flex style={{ gap: "var(--s-3)", flexWrap: "wrap", marginBottom: "var(--s-3)", padding: "var(--s-2)" }}>
        <Badge tone={RELEVANCE_TO_TONE[ticket.business_relevance] ?? "gray"}>
          Relevanz: {ticket.business_relevance}
        </Badge>
        <Badge tone="teal">Priorität: {ticket.priority}</Badge>
        <Badge tone="teal">Effort: {ticket.effort ?? "—"}</Badge>
        <Badge tone="teal">Confidence: {ticket.confidence?.toFixed(2) ?? "—"}</Badge>
        <Badge tone="blue">FV {ticket.formatversion}</Badge>
        <Badge tone="blue">{ticket.change_type ?? ""}</Badge>
      </Flex>

      {rejectMode && (
        <Card title="Verwerfen — Begründung" style={{ marginBottom: "var(--s-3)" }}>
          <Stack style={{ gap: "var(--s-2)", padding: "var(--s-3)" }}>
            <label className="prn-field-label">Warum wird dieses Ticket verworfen?</label>
            <textarea
              className="prn-input"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={3}
              placeholder="z.B. Duplicate von TICKET-XXXX, betrifft nicht unsere Komponenten, …"
            />
            <Flex style={{ gap: "var(--s-2)", justifyContent: "flex-end" }}>
              <Button variant="plain" onPress={() => setRejectMode(false)}>Doch nicht</Button>
              <Button
                variant="filled"
                isDisabled={!rejectReason.trim()}
                onPress={() => {
                  onReject(rejectReason.trim());
                  setRejectMode(false);
                }}
              >
                <Icon name={princeIcon("decline")} size={16} />
                Verwerfen bestätigen
              </Button>
            </Flex>
          </Stack>
        </Card>
      )}

      <Tabs selectedKey={tab} onSelectionChange={(k) => setTab(k as "edit" | "meta" | "diff")}>
        <TabBar>
          <Tab id="diff">Vorschau (Markdown)</Tab>
          <Tab id="meta">Quellen & Referenzen</Tab>
          <Tab id="edit">Inhalt bearbeiten</Tab>
        </TabBar>

        <TabPanel id="diff">
          <div style={{ padding: "var(--s-3)" }}>
            <div style={{ marginBottom: "var(--s-2)" }}>
              <Notice tone="info">
                Rendering ab <code>## User Story</code>. JIRA rendert Markdown ähnlich, exakte Darstellung erst nach Anlage sichtbar.
              </Notice>
            </div>
            <div
              className="markdown-preview"
              style={{
                background: "var(--sapNeutralBackground)",
                padding: "var(--s-3)",
                borderRadius: "var(--sapElement_BorderCornerRadius, 4px)",
                border: "1px solid var(--sapGroup_ContentBorderColor)",
                maxHeight: "60vh",
                overflow: "auto",
                fontSize: "0.88rem",
                lineHeight: 1.5,
              }}
              dangerouslySetInnerHTML={{ __html: previewHtml }}
            />
          </div>
        </TabPanel>

        <TabPanel id="meta">
          <Stack style={{ gap: "var(--s-3)", padding: "var(--s-3)" }}>
            <KV label="aend_id" value={ticket.aend_id} mono />
            <Flex style={{ gap: "var(--s-3)", alignItems: "baseline" }}>
              <span className="muted" style={{ fontSize: "0.78rem", minWidth: 140 }}>Quell-Datei</span>
              {ticket.source_file && docHref ? (
                <Flex style={{ gap: "var(--s-2)", alignItems: "baseline", flexWrap: "wrap" }}>
                  <a
                    href={docHref}
                    target="_blank"
                    rel="noreferrer"
                    style={{ fontFamily: "var(--font-mono)", fontSize: "0.85rem" }}
                  >
                    {ticket.source_file}
                  </a>
                  <Button
                    variant="plain"
                    onPress={() => setDocPreview(docHref)}
                  >
                    <Icon name={princeIcon("show")} size={16} />
                    Vorschau
                  </Button>
                </Flex>
              ) : (
                <span style={{ fontFamily: "var(--font-mono)", fontSize: "0.85rem" }}>
                  {ticket.source_file ?? "—"}
                </span>
              )}
            </Flex>
            <KV label="EDI@Energy ID" value={ticket.edi_energy_id ?? "—"} mono />
            <KV label="Content-Hash" value={ticket.content_hash ?? "—"} mono />
            <KV label="Doctype/Version" value={`${ticket.doctype} ${ticket.version ?? ""}`} />
            <KV label="Format" value={ticket.format || "—"} />
            <KV label="Gültig ab" value={ticket.valid_from ?? "—"} />
            <KV label="Status-Release" value={ticket.status_release_date ?? "—"} />

            <div>
              <div style={{ fontWeight: 700, fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--sapContent_LabelColor)" }}>
                Matched-Components
              </div>
              <Flex style={{ gap: 4, flexWrap: "wrap", marginTop: 6 }}>
                {(ticket.matched_components ?? []).map((c) => (
                  <Badge key={c} tone="neutral">{c}</Badge>
                ))}
                {(ticket.matched_components ?? []).length === 0 && (
                  <span className="muted">keine</span>
                )}
              </Flex>
            </div>

            <div>
              <div style={{ fontWeight: 700, fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--sapContent_LabelColor)" }}>
                Matched-Processes (EBDs)
              </div>
              <Flex style={{ gap: 4, flexWrap: "wrap", marginTop: 6 }}>
                {(ticket.matched_processes ?? []).map((p) => (
                  <Badge key={p} tone="blue">{p}</Badge>
                ))}
                {(ticket.matched_processes ?? []).length === 0 && (
                  <span className="muted">keine</span>
                )}
              </Flex>
            </div>

            <div>
              <div style={{ fontWeight: 700, fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--sapContent_LabelColor)" }}>
                Marktrollen
              </div>
              <Flex style={{ gap: 4, flexWrap: "wrap", marginTop: 6 }}>
                {(ticket.market_roles ?? []).map((r) => (
                  <Badge key={r} tone="green">{r}</Badge>
                ))}
              </Flex>
            </div>

            <div>
              <div style={{ fontWeight: 700, fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--sapContent_LabelColor)" }}>
                Review-Gründe
              </div>
              <Flex style={{ gap: 4, flexWrap: "wrap", marginTop: 6 }}>
                {(ticket.review_reasons ?? []).map((r) => (
                  <Badge key={r} tone="orange">{r}</Badge>
                ))}
                {(ticket.review_reasons ?? []).length === 0 && (
                  <span className="muted">keine — Auto-OK</span>
                )}
              </Flex>
            </div>
          </Stack>
        </TabPanel>

        <TabPanel id="edit">
          <Stack style={{ gap: "var(--s-3)", padding: "var(--s-3)" }}>
            <div>
              <TextField
                label="Titel"
                value={title}
                onChange={setTitle}
                className="prn-fill"
                style={{ width: "100%" }}
              />
              {titleChanged && <span className="muted" style={{ fontSize: "0.72rem" }}>verändert gegenüber Original</span>}
            </div>
            <div>
              <label className="prn-field-label" htmlFor="desc">Beschreibung / Body</label>
              <textarea
                id="desc"
                className="prn-input"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={22}
                style={{ width: "100%", fontFamily: "var(--font-mono)", fontSize: "0.78rem" }}
              />
              {descChanged && <span className="muted" style={{ fontSize: "0.72rem" }}>verändert gegenüber Original</span>}
            </div>
          </Stack>
        </TabPanel>
      </Tabs>

      {docPreview && (
        <Modal
          isOpen
          onOpenChange={(isOpen) => { if (!isOpen) setDocPreview(null); }}
          title={`Quell-Datei · ${ticket.source_file ?? ""}`}
          className="prn-dialog-wide"
        >
          <iframe
            src={docPreview}
            title="Quell-Datei Vorschau"
            style={{ width: "100%", height: "70vh", border: 0, background: "var(--sapNeutralBackground)" }}
          />
          <Flex justifyContent="End" style={{ gap: "var(--s-2)", marginTop: "var(--s-2)" }}>
            <Button variant="plain" onPress={() => window.open(docPreview, "_blank")}>
              Im neuen Tab öffnen
            </Button>
            <Button variant="filled" onPress={() => setDocPreview(null)}>Schließen</Button>
          </Flex>
        </Modal>
      )}

      {decision && (
        <div style={{ marginTop: "var(--s-3)" }}>
          <Notice
            tone={
              decision.state === "approved" ? "positive" :
              decision.state === "rejected" ? "negative" : "info"
            }
          >
            Aktueller Status: <strong>{decision.state}</strong>
            {decision.reviewed_at && ` · ${new Date(decision.reviewed_at).toLocaleString("de-DE")}`}
            {decision.reject_reason && ` · Grund: „${decision.reject_reason}"`}
          </Notice>
        </div>
      )}

      <Flex justifyContent="End" style={{ gap: "var(--s-2)", marginTop: "var(--s-3)" }}>
        {decision && (
          <Button variant="plain" onPress={onReset}>
            <Icon name={princeIcon("reset")} size={16} />
            Status zurücksetzen
          </Button>
        )}
        <Button variant="plain" onPress={onClose}>Abbrechen</Button>
        <Button
          variant="filled"
          onPress={() => setRejectMode(true)}
        >
          <Icon name={princeIcon("decline")} size={16} />
          Verwerfen
        </Button>
        <Button
          variant="plain"
          onPress={() => onApprove(
            titleChanged || descChanged
              ? { title: titleChanged ? title : undefined, description: descChanged ? description : undefined }
              : undefined
          )}
        >
          <Icon name={princeIcon("accept")} size={16} />
          Approve (lokal)
        </Button>
        <Button
          variant="filled"
          onPress={() => onApproveAndSync(ticket, {
            title: titleChanged ? title : undefined,
            description: descChanged ? description : undefined,
          })}
        >
          <Icon name={princeIcon("connected")} size={16} />
          Approve & in JIRA anlegen
        </Button>
      </Flex>
    </Modal>
  );
}

function KV({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <Flex style={{ gap: "var(--s-3)", alignItems: "baseline" }}>
      <span className="muted" style={{ fontSize: "0.78rem", minWidth: 140 }}>{label}</span>
      <span style={{ fontFamily: mono ? "var(--font-mono)" : undefined, fontSize: "0.85rem" }}>{value}</span>
    </Flex>
  );
}
